/**
 * How a signature field reads once a form is saved (TRUS-763).
 *
 * Shows the stored signature image with its own "Signed by … on …" line, or
 * "Not signed" (including legacy "Signed"/"Yes" text, which is not a
 * signature). When `onAdd` is given, offers Add Signature.
 */
import React from 'react';
import { CheckCircle, PenTool } from 'lucide-react';
import { useOptionalAppTranslation } from '@/contexts/TranslationContext';
import { isLegacySignatureText, isSignatureImage, type SignatureRecord } from './signatureFields';

export interface SignatureDisplayProps {
  value: unknown;
  record: SignatureRecord | null;
  onAdd?: () => void;
  addLabel?: string;
}

const formatSignedAt = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export const SignatureDisplay: React.FC<SignatureDisplayProps> = ({ value, record, onAdd, addLabel }) => {
  const { t } = useOptionalAppTranslation();
  const signed = isSignatureImage(value);
  const when = formatSignedAt(record?.signed_at);
  const who = (record?.signer_name || '').trim();

  return (
    <div>
      <div
        className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-700 text-center min-h-[100px] flex items-center justify-center"
        data-testid="signature-display"
      >
        {signed ? (
          <div>
            <img src={value as string} alt={t('forms.signature_alt', 'Signature')} className="max-h-16 mx-auto mb-1" />
            <div className="text-xs text-green-600 flex items-center justify-center gap-1">
              <CheckCircle className="w-3 h-3" />
              {who && when
                ? t('forms.signed_by_on', 'Signed by {{name}} on {{date}}', { name: who, date: when })
                : when
                  ? t('forms.signed_on', 'Signed on {{date}}', { date: when })
                  : t('forms.signature_captured', 'Signature captured')}
            </div>
          </div>
        ) : (
          <div className="text-xs text-gray-500">
            {t('forms.not_signed', 'Not signed')}
            {isLegacySignatureText(value) && (
              <div className="text-[11px] text-gray-400 mt-1">
                {t('forms.legacy_signature_text', 'The old record said "{{value}}", but no signature is on file.', {
                  value: String(value).trim(),
                })}
              </div>
            )}
          </div>
        )}
      </div>
      {!signed && onAdd && (
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 mt-2 text-xs font-medium rounded-md border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
        >
          <PenTool className="w-3 h-3" />
          {addLabel || t('forms.add_signature', 'Add Signature')}
        </button>
      )}
    </div>
  );
};
